import { useCallback, useEffect, useRef, useState } from "react";
import { i18nLanguage } from "i18n";
import { TripperWidget } from "../TripperWidget";

type TripperModalProps = {
  itineraryId: string;
  language: i18nLanguage;
  onClose: () => void;
};

export const TripperModal = ({
  itineraryId,
  language,
  onClose,
}: TripperModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);
  const [isStopDetailsOpen, setIsStopDetailsOpen] = useState(false);

  // showModal() promotes the dialog to the browser's top layer, which paints
  // above every stacking context on the host page — this is what defeats a
  // sticky nav with a high z-index.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  // showModal() makes the rest of the document inert but does not block wheel
  // scrolling on the host body, so we still lock it explicitly.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      dialogRef.current?.close();
      onClose();
    }, 150);
  }, [closing, onClose]);

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    handleClose();
  };

  // Click target === dialog only when the click landed on the ::backdrop
  // pseudo (clicks on children target the children).
  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className={`tripper-modal${closing ? " tripper-modal--closing" : ""}`}
      onCancel={handleCancel}
      onClick={handleClick}
    >
      {!isStopDetailsOpen && (
        <button
          type="button"
          className="tripper-modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      )}
      <div className="tripper-modal-body">
        <TripperWidget
          itineraryId={itineraryId}
          language={language}
          displayMode="inline"
          onStopDetailsOpenChange={setIsStopDetailsOpen}
        />
      </div>
    </dialog>
  );
};

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
